import { BaseLocationDataModel, createBaseLocationDataModel } from '../../schemas/Location.schema';
import { LocationService } from '../GooglePlaces';

/**
 * Parses location data from secondary text (e.g., "State, Country" or "Country").
 * Handles edge cases where text might be malformed or missing.
 */
function parseSecondaryText(secondaryText: string): { state: string; country: string } {
  if (!secondaryText || secondaryText.trim() === '') {
    return { state: '', country: '' };
  }

  const parts = secondaryText
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  if (parts.length === 0) {
    return { state: '', country: '' };
  }

  // If only one part, assume it's the country (not state)
  if (parts.length === 1) {
    return { state: '', country: parts[0] || '' };
  }

  // Last part is country, everything else is state
  const country = parts.pop() || '';
  const state = parts.join(', ');

  return { state, country };
}

/**
 * Validates and maps a location result to BaseLocationDataModel.
 * Returns null if the location data is invalid (e.g., missing city).
 */
function mapToLocationData(city: string, secondaryText: string): BaseLocationDataModel | null {
  const trimmedCity = city?.trim() || '';

  // City is required - return null if missing
  if (trimmedCity === '') {
    return null;
  }

  const { state, country } = parseSecondaryText(secondaryText);

  return createBaseLocationDataModel({
    city: trimmedCity,
    state,
    country,
  });
}

export async function getLocations(queryText: string): Promise<BaseLocationDataModel[]> {
  try {
    const google = await LocationService.getGoogleMaps();

    // Use the static method fetchAutocompleteSuggestions from AutocompleteSuggestion class
    // Note: AutocompleteSuggestion is not a service class - it's a data class with static methods
    const results = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: queryText,
      includedPrimaryTypes: ['locality'],
    });

    // Validate response structure
    if (!results || !results.suggestions || !Array.isArray(results.suggestions)) {
      console.warn('Invalid response structure from AutocompleteSuggestion', { results });
      return [];
    }

    return results.suggestions
      .map((suggestion: google.maps.places.AutocompleteSuggestion) => {
        const placePrediction = suggestion.placePrediction;

        // Skip if placePrediction is null
        if (!placePrediction) {
          return null;
        }

        // Extract text from FormattableText objects
        const city = placePrediction.mainText?.text || '';
        const secondaryText = placePrediction.secondaryText?.text || '';

        return mapToLocationData(city, secondaryText);
      })
      .filter((location): location is BaseLocationDataModel => location !== null);
  } catch (err) {
    console.error(err);
    return [];
  }
}
